import axios from "axios";
import type { AxiosRequestConfig, AxiosHeaders } from "axios";
import { NotificationService } from "../services/notification";
import { debounce } from "lodash";
import { processRequestQueue } from "./httpClient";
import { clearAllStorage } from "../utils/storageUtils";
import { getApiErrorMessage } from "../utils/apiErrors";
import { shouldEndSession } from "../utils/sessionErrors";
import { getFreshToken, refreshStoredToken, refreshWasRejected } from "../utils/freshToken";
import { readTenantPublicKey } from "../utils/tenantPublicKey";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * SAM-466: the caller shows its own error and needs the real status. Skips the global error
     * toast and the "404 → empty list" rewrite below (a 404 on POST /refund means
     * `payment_not_found`, never "no data"). 401 handling is unchanged.
     */
    skipGlobalErrorHandling?: boolean;
  }
}

// Configuración del cliente axios para la API de business access
const businessAccessInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}/business_access`,
});

// Notificación de errores con debounce para evitar múltiples mensajes
const debouncedNotification = debounce((message: string) => {
  NotificationService.error(message);
}, 300);

// Handle token expiration and auth conflicts
const handleTokenExpiration = () => {
  console.log("Token expired or auth conflict detected in business access, clearing all storage...");
  clearAllStorage();
  // SAM-240: see the matching comment in httpClient.ts — full reload so this
  // module's own isBusinessDataLoaded/requestQueue reset cleanly, plus the
  // query param so the login screen can show a clear "session expired" message.
  window.location.href = "/?session_expired=1";
};

// Compartir misma referencia a isBusinessDataLoaded con httpClient
let isBusinessDataLoaded = false;
const requestQueue: (() => void)[] = [];

// Function to process the queue (llamada desde BusinessContext cuando se carga la data)
export const setBusinessDataLoaded = () => {
  isBusinessDataLoaded = true;
  processRequestQueue(); // Procesar la cola del httpClient
  
  // Procesar cola local
  while (requestQueue.length > 0) {
    const request = requestQueue.shift();
    if (request) request();
  }
};

/**
 * The key for /business_access: the business's own `api_key` from businessData; else the tenant's public web key
 * (from /tenant-branding), which the backend accepts together with the caller's verified staff JWT (SAM-845); else
 * `VITE_API_KEY`. The tenant Front Desk reads its business without `api_key` (crud hides it from that role, SAM-801),
 * and the method answers 403 when it gets no key the backend knows.
 */
export const resolveApiKey = (): string | undefined => {
  let apiKey: string | undefined;
  const businessData = sessionStorage.getItem("businessData");
  if (businessData) {
    try {
      apiKey = JSON.parse(businessData).api_key || undefined;
    } catch (e) {
      console.error("Error parsing business data from sessionStorage", e);
    }
  }
  return apiKey || readTenantPublicKey() || import.meta.env.VITE_API_KEY || undefined;
};

// Configuración de interceptores para peticiones
businessAccessInstance.interceptors.request.use(async (config) => {
  const token = await getFreshToken(); // refreshed first if it expired (SAM-766)
  
  // Ensure headers exist
  if (!config.headers) {
    config.headers = new axios.AxiosHeaders();
  }
  
  if (!token) {
    return config;
  }
  
  config.headers.Authorization = `Bearer ${token}`;
  config.headers["Content-Type"] = "application/json";
  
  // Si ya tenemos datos del negocio, procesamos inmediatamente
  if (isBusinessDataLoaded) {
    const apiKey = resolveApiKey();
    if (apiKey) config.headers["X-Api-Key"] = apiKey;
    return config;
  }
  
  // La data del negocio aún no está cargada, esperar hasta que lo esté
  return new Promise((resolve) => {
    requestQueue.push(() => {
      const apiKey = resolveApiKey();
      if (apiKey) config.headers["X-Api-Key"] = apiKey;
      resolve(config);
    });
  });
});

businessAccessInstance.interceptors.response.use(
  (response) => {
    if (response.data) {
      return response.data;
    }
    return response;
  },
  async (error) => {
    // Same as httpClient (SAM-766): one refresh and a retry on a 401; a refresh the server rejects ends the session.
    const original = error?.config;
    if (original && !original._sessionRetried && error?.response?.status === 401) {
      original._sessionRetried = true;
      const fresh = await refreshStoredToken();
      if (fresh) {
        original.headers = { ...original.headers, Authorization: `Bearer ${fresh}` };
        return businessAccessInstance.request(original);
      }
      if (refreshWasRejected()) {
        handleTokenExpiration();
        return Promise.reject(error);
      }
    }
    try {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      
      // Only sign out when the session is really over (see utils/sessionErrors).
      if (shouldEndSession(error, localStorage.getItem('token'))) {
        handleTokenExpiration();
        return Promise.reject(error);
      }
      
      
      if (error.config?.skipGlobalErrorHandling) {
        return Promise.reject(error);
      }

      if (error.response?.status === 404) {
        return Promise.resolve({ 
          data: [],
          message: "No invitations available"
        });
      }
      
      if (import.meta.env.DEV) {
        console.error("API Error:", {
          url: error.config?.url,
          status: error.response?.status,
          message: error.response?.data?.message || error.message
        });
      }
      
      // SAM-544: coherent message for a 403 from the SAM-538 capability
      // checks (e.g. void/refund via portalApi) instead of a generic one —
      // see utils/apiErrors.ts.
      debouncedNotification(getApiErrorMessage(error));

      return Promise.reject(error);
    } catch (e) {
      console.error("Error in error handler:", e);
      return Promise.reject(error);
    }
  }
);

/**
 * Cliente API para realizar peticiones al servicio de business access
 * @param config Configuración de la petición Axios
 * @returns Promise con la respuesta
 */
export const businessAccessApi = <T = unknown>(config: AxiosRequestConfig): Promise<T> => {
  return businessAccessInstance.request(config) as Promise<T>;
}; 